// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Free House Strategy (FHS) cockpit, client-side helpers.
//
// FHS candidates are deal threads with assetClass === 'sfh'. The pure 9-factor
// scorer stores its result on parcelSnapshot.freeHouseScore; this module reads
// that snapshot defensively (it crosses a jsonb boundary, so nothing is typed
// for us) and exposes the recommendation buckets the cockpit groups by.
//
// CORE DOMAIN RULE: an sfh deal reaches out to the LISTING AGENT, never the
// seller. There is NO seller disposition for these deals; their lifecycle is the
// pursue / review / pass score buckets below.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

import type { DealThread } from '../../../shared/schema.js';

export type FreeHouseRecommendation = 'pursue' | 'review' | 'pass';

export interface FreeHouseScoreFactor {
  key: string;
  label: string;
  weight: number;
  raw: number;
  contribution: number;
  explanation: string;
}

export interface FreeHouseScoreView {
  total: number;
  recommendation: FreeHouseRecommendation;
  factors: FreeHouseScoreFactor[];
  guardrails: string[];
  scoredAt: string | null;
}

const RECOMMENDATION_VALUES: readonly FreeHouseRecommendation[] = ['pursue', 'review', 'pass'];

function isRecommendation(value: unknown): value is FreeHouseRecommendation {
  return typeof value === 'string' && (RECOMMENDATION_VALUES as readonly string[]).includes(value);
}

function numberOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

type FreeHouseDealLike = Pick<DealThread, 'assetClass' | 'parcelSnapshot' | 'tags'>;

/** An FHS candidate is any deal thread carrying the single-family-home asset class. */
export function isFreeHouseDeal(deal: Pick<DealThread, 'assetClass'>): boolean {
  return deal.assetClass === 'sfh';
}

/** Parse parcelSnapshot.freeHouseScore into a typed view, or null when absent/malformed. */
export function readFreeHouseScore(parcelSnapshot: unknown): FreeHouseScoreView | null {
  if (!parcelSnapshot || typeof parcelSnapshot !== 'object' || Array.isArray(parcelSnapshot)) return null;
  const raw = (parcelSnapshot as Record<string, unknown>).freeHouseScore;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const score = raw as Record<string, unknown>;

  const total = typeof score.total === 'number' && Number.isFinite(score.total) ? score.total : null;
  const recommendation = isRecommendation(score.recommendation) ? score.recommendation : null;
  if (total === null || recommendation === null) return null;

  const factors: FreeHouseScoreFactor[] = Array.isArray(score.factors)
    ? score.factors.flatMap((entry) => {
        if (!entry || typeof entry !== 'object') return [];
        const factor = entry as Record<string, unknown>;
        const key = typeof factor.key === 'string' ? factor.key : '';
        return [
          {
            key,
            label: typeof factor.label === 'string' && factor.label ? factor.label : key,
            weight: numberOrZero(factor.weight),
            raw: numberOrZero(factor.raw),
            contribution: numberOrZero(factor.contribution),
            explanation: typeof factor.explanation === 'string' ? factor.explanation : '',
          },
        ];
      })
    : [];

  const guardrails: string[] = Array.isArray(score.guardrails)
    ? score.guardrails.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : [];

  const scoredAt = typeof score.scoredAt === 'string' ? score.scoredAt : null;

  return { total, recommendation, factors, guardrails, scoredAt };
}

/** Fallback recommendation read from the free-house:pursue|review|pass tag. */
export function freeHouseRecommendationFromTags(tags: unknown): FreeHouseRecommendation | null {
  if (!Array.isArray(tags)) return null;
  for (const recommendation of RECOMMENDATION_VALUES) {
    if (tags.includes(`free-house:${recommendation}`)) return recommendation;
  }
  return null;
}

export interface FreeHouseChip {
  recommendation: FreeHouseRecommendation;
  total: number | null;
}

/**
 * The compact chip for a candidate: prefer the scored recommendation + total,
 * fall back to the tag-derived recommendation (no total). Null for non-sfh deals
 * or sfh deals that carry neither signal yet.
 */
export function freeHouseChipForDeal(deal: FreeHouseDealLike): FreeHouseChip | null {
  if (!isFreeHouseDeal(deal)) return null;
  const score = readFreeHouseScore(deal.parcelSnapshot);
  if (score) return { recommendation: score.recommendation, total: score.total };
  const fromTags = freeHouseRecommendationFromTags(deal.tags);
  if (fromTags) return { recommendation: fromTags, total: null };
  return null;
}

export interface FreeHouseRecommendationMeta {
  label: string;
  chipClass: string;
  dotClass: string;
}

// AA-contrast tokens defined in client/src/index.css. Pursue reads as a go
// signal (success/green), review as attention (warning/amber), pass as
// deprioritized (neutral/gray).
export const FREE_HOUSE_REC_META: Record<FreeHouseRecommendation, FreeHouseRecommendationMeta> = {
  pursue: { label: 'Pursue', chipClass: 'bg-success-bg text-success', dotClass: 'bg-success' },
  review: { label: 'Review', chipClass: 'bg-warning-bg text-warning', dotClass: 'bg-warning' },
  pass: { label: 'Pass', chipClass: 'bg-neutral-bg text-neutral', dotClass: 'bg-neutral' },
};

/** Bucket order for the cockpit lens: pursue first, then review, then pass. */
export const FREE_HOUSE_BUCKET_ORDER: readonly FreeHouseRecommendation[] = RECOMMENDATION_VALUES;
